import { type FC, useEffect, useState } from "react";

import {
  Heading,
  Checkbox,
  Button,
  Text,
  Row,
  Box,
  Column,
  CheckboxGroup,
  Paragraph,
  ArrowRightIcon,
  BidirectionalArrowIcon,
  SuccessIcon,
  HtImage,
} from "@hightouch/htui";
import { Link } from "src/router";
import { Navigate, useParams, useNavigate } from "src/router";

import { AuthLayout } from "src/components/auth/auth-layout";
import {
  useAccessibleOrganizationsQuery,
  usePartnerConnectGetConnectionQuery,
} from "src/graphql";
import { Logo } from "src/ui/brand";
import { PageSpinner } from "src/components/loading";
import { switchWorkspace } from "src/utils/workspaces";
import { AuthCard } from "src/components/auth/auth-card";

const PartnerConnect: FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAcceptedSharing, setHasAcceptedSharing] = useState(false);

  const {
    data: partnerConnection,
    isLoading: isPartnerConnectionLoading,
    error: partnerConnectionError,
  } = usePartnerConnectGetConnectionQuery(
    { uuid },
    {
      select: (data) => data.partnerConnectGetConnection,
      enabled: Boolean(uuid),
      cacheTime: 0,
    },
  );

  const { data: organizations, isLoading: isOrganizationsLoading } =
    useAccessibleOrganizationsQuery(
      {},
      { select: (data) => data.getAccessibleOrganizations.organizations },
    );

  if (isPartnerConnectionLoading || isOrganizationsLoading) {
    return <PageSpinner />;
  }

  const workspaces = organizations?.flatMap((org) => org.workspaces) ?? [];

  const linkedWorkspace = workspaces?.find(
    (w) => String(w?.id) === String(partnerConnection?.workspaceId),
  );

  if (partnerConnectionError) {
    return (
      <Navigate
        state={{ error: partnerConnectionError.message }}
        to="/unauthorized"
      />
    );
  }

  if (partnerConnection?.workspaceId) {
    if (!linkedWorkspace) {
      return (
        <Navigate
          state={{
            error: `You are not authorized to access the workspace ${partnerConnection.partnerName} is connected to.`,
          }}
          to="/unauthorized"
        />
      );
    }

    // If there's a returnUrl (e.g. HubSpot marketplace flow), auto-redirect
    // back to the partner after workspace linking is complete.
    if (partnerConnection.returnUrl) {
      return (
        <PartnerReturnRedirect
          returnUrl={partnerConnection.returnUrl}
          partnerName={partnerConnection.partnerName}
          partnerLogo={partnerConnection.partnerLogo}
        />
      );
    }

    return (
      <AuthLayout
        metaTitle={`Connect Hightouch to ${partnerConnection?.partnerName}`}
      >
        <AuthCard>
          <Column align="center" gap={12}>
            <PartnerConnectLogo
              logo={partnerConnection.partnerLogo}
              name={partnerConnection.partnerName}
            />
            <Heading size="2xl">
              {partnerConnection.partnerName} connected!
            </Heading>
            <Paragraph textAlign="center">
              Your Hightouch workspace <strong>"{linkedWorkspace.name}"</strong>{" "}
              is connected to {partnerConnection.partnerName}.
            </Paragraph>
            <Button
              size="lg"
              variant="primary"
              onClick={async () => {
                if (
                  partnerConnection.workspaceId &&
                  linkedWorkspace.is_member
                ) {
                  await switchWorkspace(
                    partnerConnection.workspaceId,
                    `/${linkedWorkspace.slug}`,
                  );
                }
              }}
            >
              Go to workspace
            </Button>
          </Column>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (!partnerConnection) {
    return null;
  }

  return (
    <AuthLayout
      metaTitle={`Connect Hightouch to ${partnerConnection?.partnerName}`}
    >
      <AuthCard
        heading={`Connect Hightouch to ${partnerConnection?.partnerName}`}
        gap={6}
      >
        <Column gap={12} align="center">
          <PartnerConnectLogo
            logo={partnerConnection?.partnerLogo}
            name={partnerConnection?.partnerName}
          />

          <PartnerTermsCheckboxes
            partnerName={partnerConnection.partnerName}
            hasAcceptedTerms={hasAcceptedTerms}
            hasAcceptedSharing={hasAcceptedSharing}
            onTermsChange={setHasAcceptedTerms}
            onSharingChange={setHasAcceptedSharing}
          />

          <Button
            directionIcon={ArrowRightIcon}
            isDisabled={!(hasAcceptedSharing && hasAcceptedTerms)}
            size="lg"
            variant="primary"
            onClick={() => {
              const workspaceCount = workspaces.length ?? 0;
              navigate(workspaceCount > 0 ? "/workspaces" : "/workspaces/new", {
                state: { partnerConnection: partnerConnection },
              });
            }}
          >
            Continue
          </Button>
        </Column>
      </AuthCard>
    </AuthLayout>
  );
};

export default PartnerConnect;

const PartnerReturnRedirect: FC<
  Readonly<{ returnUrl: string; partnerName: string; partnerLogo: string }>
> = ({ returnUrl, partnerName, partnerLogo }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.href = returnUrl;
    }, 1500);
    return () => clearTimeout(timer);
  }, [returnUrl]);

  return (
    <AuthLayout metaTitle={`Connected to ${partnerName}`}>
      <AuthCard>
        <Column align="center" gap={12}>
          <PartnerConnectLogo logo={partnerLogo} name={partnerName} />
          <Heading size="2xl">{partnerName} connected!</Heading>
          <Paragraph textAlign="center">
            Redirecting back to {partnerName}…
          </Paragraph>
        </Column>
      </AuthCard>
    </AuthLayout>
  );
};

export const PartnerTermsCheckboxes: FC<
  Readonly<{
    partnerName: string;
    hasAcceptedTerms: boolean;
    hasAcceptedSharing: boolean;
    onTermsChange: (checked: boolean) => void;
    onSharingChange: (checked: boolean) => void;
  }>
> = ({
  partnerName,
  hasAcceptedTerms,
  hasAcceptedSharing,
  onTermsChange,
  onSharingChange,
}) => (
  <Column textAlign="left">
    <CheckboxGroup>
      <Checkbox
        // biome-ignore lint/suspicious/noTsIgnore: legacy suppression
        // @ts-ignore
        description={
          <>
            I agree to Hightouch's{" "}
            <Link
              href={`${import.meta.env.VITE_WEBSITE_URL}/terms-of-service/`}
              rel="noreferrer"
              target="_blank"
            >
              terms of service
            </Link>{" "}
            related to its usage in conjunction with {partnerName}.
          </>
        }
        isChecked={hasAcceptedTerms}
        label="Terms of service"
        onChange={(event) => onTermsChange(event.target.checked)}
      />
      <Checkbox
        description={`I acknowledge that Hightouch will share data on its usage with ${partnerName}`}
        isChecked={hasAcceptedSharing}
        label="Usage sharing"
        onChange={(event) => onSharingChange(event.target.checked)}
      />
    </CheckboxGroup>
  </Column>
);

export const PartnerConnectLogo: FC<
  Readonly<{ name: string; logo: string }>
> = ({ name, logo }) => (
  <Row align="center" gap={4} mx="auto">
    <Logo size="48px" theme="dark" />
    <Box fontSize="36px">
      <BidirectionalArrowIcon color="text.tertiary" />
    </Box>
    <HtImage
      alt={name}
      src={logo}
      sx={{ width: "48px", objectFit: "contain" }}
    />
  </Row>
);

export const PartnerConnectHeader: FC<
  Readonly<{ partnerInformation: any }>
> = ({ partnerInformation }) => {
  return (
    <Column align="center" color="text.secondary" gap={8} mb={8}>
      <PartnerConnectLogo
        logo={partnerInformation.partnerLogo}
        name={partnerInformation.partnerName}
      />

      <Column gap={2}>
        {partnerInformation.resources.map((resource) => (
          <Row key={resource} align="center" gap={2}>
            <Box fontSize="24px">
              <SuccessIcon color="primary.base" />
            </Box>
            <Text fontWeight="medium">
              Connect your {resource.split(" ").slice(1).join(" ")}
            </Text>
          </Row>
        ))}
      </Column>
    </Column>
  );
};
